import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuoteStore } from '../stores/quoteStore';
import { getQuoteStatus, isQuoteActive, splitQuotesByActivity } from '../utils/quoteUtils';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { cleanupModalParamsFromUrl, decodeModalParams } from '../utils/modalUrlParams';
import bgImageUrl from '../assets/images/arrierePlan2.png';
import QuoteConfirmActivateModal from '../components/quotes/QuoteConfirmActivateModal';
import { Quote } from '../types/quote';
import FormattedCurrency from '../components/common/FormattedCurrency';
import ProfileAvatar from '../components/common/ProfileAvatar';
import { computeQuoteAmount, getJobPublishedInfo } from '../utils/jobUtils';
import { useJobSearchStore } from '../stores/jobSearchStore';

const STEP_KEYS = ['one', 'two', 'three'] as const;
const MOBILE_INITIAL_VISIBLE_QUOTES = 3;
const MOBILE_VISIBLE_INCREMENT = 3;
const AVATAR_COLOR_CLASSES = ['bg-primary', 'bg-mauve', 'bg-secondary'];

const getQuoteInitials = (title: string): string => {
  const words = title.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return 'OF';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
};

const getAvatarColorClass = (index: number): string =>
  AVATAR_COLOR_CLASSES[index % AVATAR_COLOR_CLASSES.length];

const isMobileViewport = (): boolean => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(max-width: 767px)').matches;
};

const getQuoteStatusBadgeClass = (status: string): string => {
  const greyStatuses = ['rejected', 'retired', 'not_accepted', 'job_cancelled', 'alliance_retired'];
  if (greyStatuses.includes(status)) return 'bg-[#0D2D390D] text-[#304659]';
  if (status === 'accepted') return 'bg-primary text-white';
  return 'bg-[#0D2D390D] text-[#304659]';
};

const getQuoteManagePath = (quote: Quote): string =>
  `/projects/${quote.job_id}?selectedQuoteId=${quote.id}`;

const Quotes: React.FC = () => {
  const { t, i18n } = useTranslation(['quotes', 'common']);
  const { quotes, fetchQuotes, loading, error } = useQuoteStore();
  const { fetch_spotlighted_jobs } = useJobSearchStore();

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [mobileVisibleQuotes, setMobileVisibleQuotes] = useState(MOBILE_INITIAL_VISIBLE_QUOTES);
  const [mobileLayoutEnabled, setMobileLayoutEnabled] = useState(isMobileViewport);
  const [quoteToActivate, setQuoteToActivate] = useState<Quote | null>(null);

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const openManageQuote = useCallback(
    (quote: Quote) => {
      navigate(getQuoteManagePath(quote));
    },
    [navigate]
  );

  useEffect(() => {
    fetchQuotes();
    fetch_spotlighted_jobs();
  }, [fetchQuotes, fetch_spotlighted_jobs]);

  useEffect(() => {
    const handleResize = () => setMobileLayoutEnabled(isMobileViewport());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const selectedId = (location.state as { selected_id?: string } | null)?.selected_id;
    const jobIdParam = searchParams.get('jobId');
    const modalParams = decodeModalParams(searchParams);

    const targetId =
      selectedId || jobIdParam || (modalParams?.type === 'quote' ? modalParams.quoteId : null);

    if (!targetId || !quotes.length || loading) return;

    const foundQuote = quotes.find(q => q.id === targetId || q.job_id === targetId);

    if (foundQuote) {
      if (foundQuote.status === 'retired') {
        setQuoteToActivate(foundQuote);
      } else {
        openManageQuote(foundQuote);
      }

      if (jobIdParam || modalParams) {
        cleanupModalParamsFromUrl(searchParams, setSearchParams);
      }
    }
  }, [location.state, loading, openManageQuote, quotes, searchParams, setSearchParams]);

  const stepEntries = useMemo(
    () =>
      STEP_KEYS.map((key, index) => ({
        id: index + 1,
        title: t(`bigSteps.steps.${key}.title`),
        description: t(`bigSteps.steps.${key}.description`),
      })),
    [t]
  );

  const visibleQuotes = useMemo(() => quotes.filter(q => q.status !== 'retired'), [quotes]);
  const { activeQuotes, inactiveQuotes } = useMemo(
    () => splitQuotesByActivity(visibleQuotes),
    [visibleQuotes]
  );
  const sortedQuotes = useMemo(
    () => [...activeQuotes, ...inactiveQuotes],
    [activeQuotes, inactiveQuotes]
  );

  const currentStep = stepEntries[currentStepIndex];
  const quotesToRender = mobileLayoutEnabled
    ? sortedQuotes.slice(0, mobileVisibleQuotes)
    : sortedQuotes;

  const renderQuoteCard = (quote: Quote, index: number) => {
    const status = isQuoteActive(quote) ? 'pending' : getQuoteStatus(quote);
    const statusLabel =
      status === 'pending'
        ? t('status.pending', { ns: 'common' })
        : t(`quote_statuses.${status}`, { ns: 'common', defaultValue: status });

    const isChild = !!quote.parent_quote_id;
    const ownershipLabel = isChild
      ? t('quote_ownership_statuses.alliance')
      : t('quote_ownership_statuses.principal');

    const { publishedDate } = getJobPublishedInfo(quote.job, t, i18n.language);
    const teamMembers = quote.alliances?.map(a => a.invited_freelancer) || [];

    return (
      <article
        key={quote.id}
        className="rounded-[18px] border-2 border-[#BFD3DE] bg-white p-4 shadow-[0px_3px_12px_rgba(13,45,57,0.14)] md:p-6"
      >
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="flex min-w-0 flex-col gap-4 md:flex-1 md:flex-row md:items-center md:gap-6">
            <div className="flex min-w-0 items-center gap-4 md:flex-1">
              <div
                className={`${getAvatarColorClass(index)} flex size-[52px] shrink-0 items-center justify-center rounded-full text-[22px] font-bold text-white shadow-sm`}
              >
                {getQuoteInitials(quote.job.title)}
              </div>

              <div className="min-w-0">
                <h2 className="truncate text-xl font-semibold text-[#304659] md:text-[24px]">
                  {quote.job.title}
                </h2>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <span className="text-[13px] font-semibold text-[#304659]">
                    {publishedDate || t('quoteRequestInfo.publishedDate')}
                  </span>
                  <div className="flex gap-2">
                    <span
                      className={`rounded-full px-3 py-0.5 text-[12px] font-medium ${isChild ? 'border border-primary text-primary' : 'bg-primary text-white'}`}
                    >
                      {ownershipLabel}
                    </span>
                    <span
                      className={`rounded-full px-3 py-0.5 text-[12px] font-medium ${getQuoteStatusBadgeClass(status)}`}
                    >
                      {statusLabel}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-6 border-t border-gray-100 pt-4 md:border-none md:pt-0">
              <div className="min-w-[100px]">
                <p className="text-[12px] uppercase tracking-wider text-[#304659]/70">
                  {t('offerLabel')}
                </p>
                <p className="text-lg font-bold text-[#304659]">
                  <FormattedCurrency value={computeQuoteAmount(quote)} />
                </p>
              </div>

              {teamMembers.length > 0 && (
                <div className="flex flex-col gap-1">
                  <p className="text-[12px] uppercase tracking-wider text-[#304659]/70">
                    {t('teamMembers')}
                  </p>
                  <div className="flex -space-x-2">
                    {teamMembers.slice(0, 4).map(member => (
                      <ProfileAvatar
                        key={member.profile_id}
                        profile_id={member.profile_id}
                        first_name={member.first_name}
                        avatar_url={member.avatar_url}
                        initials={
                          member.first_name ? member.first_name.charAt(0).toUpperCase() : '?'
                        }
                        className="size-8 border-2 border-white bg-[#3B5266] text-[11px] font-bold leading-none text-white"
                      />
                    ))}
                    {teamMembers.length > 4 && (
                      <div className="flex size-8 items-center justify-center rounded-full bg-gray-100 text-[11px] font-bold text-[#304659]">
                        +{teamMembers.length - 4}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="shrink-0">
            <Link
              to={getQuoteManagePath(quote)}
              className="inline-flex w-full justify-center rounded-full bg-primary px-8 py-2.5 text-[16px] font-semibold text-white transition-all hover:bg-primary/90 active:scale-95 md:w-auto"
            >
              {t('manage')}
            </Link>
          </div>
        </div>
      </article>
    );
  };

  return (
    <div className="mb-8 flex flex-col gap-6 px-4 md:min-h-[calc(100vh-4rem)] md:px-5 lg:px-32">
      <div className="flex w-full flex-col pb-2 pt-4">
        <h1 className="text-[45px] font-semibold leading-[55px] text-[#304659] md:text-[95px] md:leading-[100px]">
          {t('title')}
        </h1>
      </div>

      <section className="overflow-hidden rounded-[25px] bg-white shadow-[0px_3px_12px_rgba(13,45,57,0.14)]">
        <div className="relative">
          <img src={bgImageUrl} alt="" className="h-[500px] w-full object-cover" />
          <div className="absolute inset-0 flex flex-col justify-center gap-4 p-4 md:flex-row md:items-start md:justify-between md:p-8">
            <div className="hidden h-fit w-full rounded-[25px] bg-white p-6 shadow-md md:block md:max-w-[35%] lg:max-w-[40%]">
              <h2 className="text-[25px] font-semibold leading-tight text-[#304659]">
                {t('pendingRequestsTitle')}
              </h2>
              <p className="mt-4 text-center text-[15px] text-[#304659]">
                {t('pending_requests_empty')}
              </p>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => navigate('/prospects')}
                  className="h-10 rounded-full border border-primary/30 px-5 text-[13px] font-semibold text-primary"
                >
                  {t('viewProspectsButton')}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white px-4 py-6 md:px-8">
          <h2 className="text-[25px] font-bold text-[#304659]">{t('bigSteps.title')}</h2>
          <p className="text-[15px] text-[#304659]">{t('bigSteps.subtitle')}</p>

          <div className="mt-4 hidden grid-cols-3 gap-6 md:grid">
            {stepEntries.map(step => (
              <article key={step.id} className="flex gap-3">
                <p className="text-[70px] font-black leading-none text-[#304659]">{step.id}</p>
                <div className="pt-2">
                  <h3 className="text-[17px] font-bold text-[#304659]">{step.title}</h3>
                  <p className="mt-1 text-[15px] leading-5 text-[#3B5266]">{step.description}</p>
                </div>
              </article>
            ))}
          </div>

          {currentStep && (
            <div className="mt-4 md:hidden">
              <article className="flex gap-3">
                <p className="text-[70px] font-black leading-none text-[#304659]">
                  {currentStep.id}
                </p>
                <div className="pt-2">
                  <h3 className="text-[17px] font-bold text-[#304659]">{currentStep.title}</h3>
                  <p className="mt-1 text-[15px] text-[#3B5266]">{currentStep.description}</p>
                </div>
              </article>
              <div className="mt-4 flex justify-center gap-2">
                <button
                  onClick={() => setCurrentStepIndex(i => (i === 0 ? 2 : i - 1))}
                  className="h-9 w-11 rounded-l-full bg-primary/30 text-primary"
                >
                  ←
                </button>
                <button
                  onClick={() => setCurrentStepIndex(i => (i === 2 ? 0 : i + 1))}
                  className="h-9 w-11 rounded-r-full bg-primary text-white"
                >
                  →
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="flex flex-col gap-4">
        <p className="text-[13px] font-semibold uppercase tracking-wider text-[#304659]">
          {t('published_quotes_title')}
        </p>

        {loading && quotes.length === 0 && <LoadingSpinner />}
        {error && <div className="text-center text-red-600">{error}</div>}

        {!loading && sortedQuotes.length === 0 && (
          <div className="rounded-[18px] border-2 border-[#BFD3DE] bg-white py-12 text-center text-[#304659]">
            {t('noQuotes')}
          </div>
        )}

        <div className="flex flex-col gap-4">
          {quotesToRender.map((q, i) => renderQuoteCard(q, i))}
          {mobileLayoutEnabled && sortedQuotes.length > mobileVisibleQuotes && (
            <button
              onClick={() => setMobileVisibleQuotes(v => v + MOBILE_VISIBLE_INCREMENT)}
              className="h-10 rounded-full bg-primary font-semibold text-white"
            >
              {t('load_more_quotes')}
            </button>
          )}
        </div>
      </section>

      {quoteToActivate && (
        <QuoteConfirmActivateModal
          quote={quoteToActivate}
          onClose={() => setQuoteToActivate(null)}
          onActivated={() => {
            openManageQuote(quoteToActivate);
            setQuoteToActivate(null);
          }}
        />
      )}
    </div>
  );
};

export default Quotes;
